import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  AfterViewChecked,
} from '@angular/core';
import { Subscription } from 'rxjs';

import { ChatbotStateService } from 'src/app/_services/chatbot/chatbot-state.service';
import { ChatbotWebsocketService, ConnectionStatus } from 'src/app/_services/chatbot/chatbot-websocket.service';
import { ChatbotApiService } from 'src/app/_services/chatbot/chatbot-api.service';
import { AuthService } from 'src/app/_services/auth/auth.service';
import { ChatbotMessage } from 'src/app/interfaces/chatbot-message.interfaces';
import { ChatbotSession } from 'src/app/interfaces/chatbot-session.interfaces';

/**
 * ChatbotPanelComponent — Panel deslizable principal del chatbot.
 *
 * Muestra la lista de sesiones anteriores, el área de mensajes de la sesión
 * activa y el campo de entrada de texto con el botón de envío.
 *
 * Requisitos: 3.3, 4.2, 4.3, 5.3, 5.4, 5.5, 5.6, 5.7, 5.8, 5.9
 */
@Component({
  selector: 'app-chatbot-panel',
  templateUrl: './chatbot-panel.component.html',
  styleUrls: ['./chatbot-panel.component.css'],
})
export class ChatbotPanelComponent implements OnInit, OnDestroy, AfterViewChecked {

  // ---------------------------------------------------------------------------
  // ViewChild — contenedor de mensajes para auto-scroll y textarea para foco
  // ---------------------------------------------------------------------------

  @ViewChild('messagesContainer') private messagesContainer!: ElementRef<HTMLDivElement>;

  /** Referencia al textarea de entrada para devolver el foco tras validación. */
  @ViewChild('messageInput') private messageInput!: ElementRef<HTMLTextAreaElement>;

  // ---------------------------------------------------------------------------
  // Estado del componente
  // ---------------------------------------------------------------------------

  /** Texto actual del campo de entrada. */
  inputText = '';

  /** Lista de mensajes de la sesión activa. */
  messages: ChatbotMessage[] = [];

  /** Lista de sesiones del usuario. */
  sessions: ChatbotSession[] = [];

  /** Sesión actualmente seleccionada. */
  activeSession: ChatbotSession | null = null;

  /** Estado de conexión WebSocket. */
  connectionStatus: ConnectionStatus = 'disconnected';

  /** Indica si se está esperando el primer token de respuesta. */
  isLoading = false;

  /** Indica si se está cargando el historial de una sesión seleccionada. */
  isLoadingHistory = false;

  /** Controla la visibilidad del drawer de sesiones en tablet/móvil. */
  isDrawerOpen = false;

  /** Controla si se debe hacer scroll al fondo en el próximo ciclo de detección. */
  private shouldScrollToBottom = false;

  /** Cola de caracteres pendientes para el efecto de typing suave. */
  private _typingQueue: string[] = [];

  /** Indica si el bucle de typing está activo. */
  private _typingActive = false;

  /** ID del requestAnimationFrame / setTimeout activo para typing. */
  private _typingTimer: ReturnType<typeof setTimeout> | null = null;

  /** Velocidad de typing en ms por carácter (menor = más rápido). */
  private readonly _typingSpeed = 20;

  // ---------------------------------------------------------------------------
  // Suscripciones
  // ---------------------------------------------------------------------------

  private subscriptions = new Subscription();

  // ---------------------------------------------------------------------------
  // Constructor e inyección de dependencias
  // ---------------------------------------------------------------------------

  constructor(
    private stateService: ChatbotStateService,
    private wsService: ChatbotWebsocketService,
    private apiService: ChatbotApiService,
    private authService: AuthService,
  ) {}

  // ---------------------------------------------------------------------------
  // Ciclo de vida
  // ---------------------------------------------------------------------------

  ngOnInit(): void {
    this._subscribeToState();
    this._loadSessions();
    this._connectWebSocket();
    this._subscribeToTokenStream();
    this._subscribeToConnectionStatus();
  }

  ngAfterViewChecked(): void {
    if (this.shouldScrollToBottom) {
      this._scrollToBottom();
      this.shouldScrollToBottom = false;
    }
  }

  ngOnDestroy(): void {
    this._flushTypingQueue();
    this.subscriptions.unsubscribe();
  }

  // ---------------------------------------------------------------------------
  // Acciones del usuario
  // ---------------------------------------------------------------------------

  /**
   * Envía el mensaje actual al backend vía WebSocket.
   * Req 5.9: previene el envío si el mensaje está vacío o solo tiene espacios
   * en blanco, y devuelve el foco al campo de entrada.
   */
  sendMessage(): void {
    const trimmed = this.inputText.trim();
    if (!trimmed) {
      // Devolver el foco al textarea para que el usuario pueda escribir
      // inmediatamente sin necesidad de hacer clic de nuevo (Req 5.9).
      this._focusInput();
      return;
    }

    // Si no hay sesión activa, crear una nueva antes de enviar.
    const sessionId = this.activeSession?.session_id ?? this._generateSessionId();

    if (!this.activeSession) {
      const newSession: ChatbotSession = {
        session_id: sessionId,
        user_id: '',
        title: trimmed.substring(0, 60),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      this.stateService.setActiveSession(newSession);
      this.stateService.prependSession(newSession);
    }

    // Agregar el mensaje del usuario al estado local inmediatamente.
    const userMessage: ChatbotMessage = {
      session_id: sessionId,
      user_id: '',
      role: 'user',
      content: trimmed,
      timestamp: new Date().toISOString(),
    };
    this.stateService.addMessage(userMessage);

    // Agregar un mensaje vacío del asistente que se irá llenando con tokens.
    const assistantMessage: ChatbotMessage = {
      session_id: sessionId,
      user_id: '',
      role: 'assistant',
      content: '',
      timestamp: new Date().toISOString(),
    };
    this.stateService.addMessage(assistantMessage);
    this.stateService.setLoading(true);

    // Limpiar el input y enviar por WebSocket.
    this.inputText = '';
    this.wsService.sendMessage(trimmed, sessionId);
    this.shouldScrollToBottom = true;
  }

  /**
   * Selecciona una sesión existente y carga su historial de mensajes.
   * Req 4.3: al seleccionar una sesión se carga el historial completo.
   *
   * @param session  Sesión a activar.
   */
  selectSession(session: ChatbotSession): void {
    if (this.activeSession?.session_id === session.session_id) {
      return;
    }

    this._flushTypingQueue();
    this.stateService.setActiveSession(session);
    this.stateService.setMessages([]);
    this.isLoadingHistory = true;

    this.apiService.getSessionMessages(session.session_id).subscribe({
      next: (messages) => {
        this.stateService.setMessages(messages);
        this.isLoadingHistory = false;
        this.shouldScrollToBottom = true;
      },
      error: () => {
        // Si falla la carga, mostrar arreglo vacío sin romper la UI.
        this.stateService.setMessages([]);
        this.isLoadingHistory = false;
      },
    });
  }

  /**
   * Crea una nueva sesión de conversación vacía.
   */
  createNewSession(): void {
    this._flushTypingQueue();
    this.stateService.setActiveSession(null);
    this.stateService.setMessages([]);
    this.inputText = '';
  }

  /** Abre/cierra el drawer de sesiones en tablet/móvil. */
  toggleDrawer(): void {
    this.isDrawerOpen = !this.isDrawerOpen;
  }

  /** Cierra el drawer. */
  closeDrawer(): void {
    this.isDrawerOpen = false;
  }

  /** Selecciona sesión desde el drawer y lo cierra. */
  onDrawerSessionSelected(session: ChatbotSession): void {
    this.selectSession(session);
    this.closeDrawer();
  }

  /** Nueva sesión desde el drawer y lo cierra. */
  onDrawerNewSession(): void {
    this.createNewSession();
    this.closeDrawer();
  }

  /**
   * Maneja el evento keydown en el textarea.
   * Enter sin Shift envía el mensaje; Shift+Enter inserta salto de línea.
   */
  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  // ---------------------------------------------------------------------------
  // Helpers de plantilla
  // ---------------------------------------------------------------------------

  /**
   * TrackBy para el *ngFor de mensajes. Usa el índice para evitar que Angular
   * destruya y recree el componente del último mensaje durante el streaming
   * de tokens (donde el contenido cambia carácter por carácter).
   */
  trackByIndex(index: number): number {
    return index;
  }

  /** Devuelve la etiqueta de estado de conexión en español. */
  get connectionStatusLabel(): string {
    switch (this.connectionStatus) {
      case 'connected':     return 'Conectado';
      case 'reconnecting':  return 'Reconectando…';
      case 'disconnected':  return 'Desconectado';
    }
  }

  /** Devuelve la clase CSS del badge de estado de conexión. */
  get connectionStatusClass(): string {
    switch (this.connectionStatus) {
      case 'connected':     return 'badge-connected';
      case 'reconnecting':  return 'badge-reconnecting';
      case 'disconnected':  return 'badge-disconnected';
    }
  }

  /** Indica si el botón de envío debe estar deshabilitado. */
  get isSendDisabled(): boolean {
    return !this.inputText.trim() || this.isLoading;
  }

  // ---------------------------------------------------------------------------
  // Métodos privados
  // ---------------------------------------------------------------------------

  /** Suscribe el componente a los observables del estado centralizado. */
  private _subscribeToState(): void {
    this.subscriptions.add(
      this.stateService.messages$.subscribe((msgs) => {
        this.messages = msgs;
        this.shouldScrollToBottom = true;
      })
    );

    this.subscriptions.add(
      this.stateService.sessions$.subscribe((sessions) => {
        this.sessions = sessions;
      })
    );

    this.subscriptions.add(
      this.stateService.activeSession$.subscribe((session) => {
        this.activeSession = session;
      })
    );

    this.subscriptions.add(
      this.stateService.isLoading$.subscribe((loading) => {
        this.isLoading = loading;
      })
    );

    this.subscriptions.add(
      this.stateService.connectionStatus$.subscribe((status) => {
        this.connectionStatus = status;
      })
    );
  }

  /**
   * Carga la lista de sesiones del usuario desde el backend.
   * Req 4.2: al abrir el componente se recuperan las sesiones anteriores.
   */
  private _loadSessions(): void {
    this.apiService.getSessions().subscribe({
      next: (sessions) => {
        this.stateService.setSessions(sessions);
        // No se selecciona ninguna sesión automáticamente.
        // El usuario verá el estado vacío ("¿En qué puedo ayudarte?")
        // y podrá elegir una conversación anterior desde el sidebar.
      },
      error: () => {
        // Si falla la carga de sesiones, continuar con lista vacía.
        this.stateService.setSessions([]);
      },
    });
  }

  /**
   * Conecta el WebSocket usando el JWT almacenado en localStorage.
   * Req 3.3: conexión WebSocket para recibir tokens en tiempo real.
   */
  private _connectWebSocket(): void {
    const token = this.authService.getToken();
    // Eliminar el prefijo "GMD-" si está presente, ya que el servicio lo maneja.
    const rawToken = token.startsWith('GMD-') ? token.substring(4) : token;
    this.wsService.connect(rawToken);
  }

  /**
   * Puente entre el estado de conexión del WebSocket y el servicio de estado
   * centralizado. Req 5.6: el badge de estado refleja el estado real del socket.
   * Req 5.5: si se pierde la conexión mientras se espera respuesta, se cancela
   * el indicador de carga para no dejar la UI bloqueada.
   */
  private _subscribeToConnectionStatus(): void {
    this.subscriptions.add(
      this.wsService.connectionStatus$.subscribe((status) => {
        this.stateService.setConnectionStatus(status);

        // Si la conexión se pierde inesperadamente mientras se esperaba el
        // primer token, desactivar el indicador de carga (Req 5.5).
        if (status === 'disconnected' && this.stateService.isLoading) {
          this.stateService.setLoading(false);
        }
      })
    );
  }

  /**
   * Suscribe el componente al stream de tokens del WebSocket para renderizado
   * progresivo de respuestas del asistente.
   * Req 5.4: renderizado progresivo de tokens.
   * Req 5.5: indicador de carga hasta el primer token.
   */
  private _subscribeToTokenStream(): void {
    // Tokens entrantes: encolar caracteres para efecto de typing suave.
    this.subscriptions.add(
      this.wsService.tokenStream$.subscribe((token) => {
        // Al llegar el primer token, desactivar el indicador de carga.
        if (this.isLoading) {
          this.stateService.setLoading(false);
        }

        // Encolar cada carácter del chunk para renderizado progresivo.
        for (const char of token) {
          this._typingQueue.push(char);
        }

        // Iniciar el bucle de typing si no está activo.
        if (!this._typingActive) {
          this._typingActive = true;
          this._processTypingQueue();
        }
      })
    );

    // Respuesta completa: vaciar la cola de typing de inmediato.
    this.subscriptions.add(
      this.wsService.messageComplete$.subscribe(() => {
        this._flushTypingQueue();
        this.stateService.setLoading(false);
        this.shouldScrollToBottom = true;
      })
    );

    // Errores del WebSocket: desactivar loading y mostrar mensaje de error.
    this.subscriptions.add(
      this.wsService.error$.subscribe((error) => {
        this._flushTypingQueue();
        this.stateService.setLoading(false);
        if (error.code !== 'AUTH_ERROR') {
          // Agregar mensaje de error como mensaje del asistente.
          const current = this.stateService.messages;
          if (current.length > 0 && current[current.length - 1].role === 'assistant' && current[current.length - 1].content === '') {
            this.stateService.updateLastMessageContent(
              'Lo siento, ocurrió un error al procesar tu consulta. Por favor, intenta de nuevo.'
            );
          }
        }
      })
    );
  }

  /**
   * Procesa la cola de caracteres uno por uno con un delay entre cada uno,
   * creando un efecto de typing fluido en lugar de volcar chunks completos.
   */
  private _processTypingQueue(): void {
    if (this._typingQueue.length === 0) {
      this._typingActive = false;
      return;
    }

    // Tomar un solo carácter a la vez para un efecto de typing más pausado.
    const char = this._typingQueue.shift()!;

    const current = this.stateService.messages;
    if (current.length > 0) {
      const last = current[current.length - 1];
      if (last.role === 'assistant') {
        this.stateService.updateLastMessageContent(last.content + char);
        this.shouldScrollToBottom = true;
      }
    }

    this._typingTimer = setTimeout(() => {
      this._processTypingQueue();
    }, this._typingSpeed);
  }

  /**
   * Vacía la cola de typing de inmediato, volcando todo el texto pendiente
   * al mensaje. Se usa cuando llega messageComplete o un error.
   */
  private _flushTypingQueue(): void {
    if (this._typingTimer !== null) {
      clearTimeout(this._typingTimer);
      this._typingTimer = null;
    }

    if (this._typingQueue.length > 0) {
      const remaining = this._typingQueue.splice(0).join('');
      const current = this.stateService.messages;
      if (current.length > 0) {
        const last = current[current.length - 1];
        if (last.role === 'assistant') {
          this.stateService.updateLastMessageContent(last.content + remaining);
          this.shouldScrollToBottom = true;
        }
      }
    }

    this._typingActive = false;
  }

  /** Desplaza el contenedor de mensajes al fondo. */
  private _scrollToBottom(): void {
    try {
      if (this.messagesContainer) {
        this.messagesContainer.nativeElement.scrollTop =
          this.messagesContainer.nativeElement.scrollHeight;
      }
    } catch {
      // Ignorar errores de scroll (el elemento puede no estar en el DOM aún).
    }
  }

  /**
   * Devuelve el foco al textarea de entrada.
   * Req 5.9: mantener el foco en el campo de entrada tras un intento de envío
   * bloqueado por validación (mensaje vacío o solo espacios en blanco).
   */
  private _focusInput(): void {
    try {
      if (this.messageInput) {
        this.messageInput.nativeElement.focus();
      }
    } catch {
      // Ignorar si el elemento no está disponible en el DOM.
    }
  }

  /** Genera un UUID v4 simple para nuevas sesiones creadas en el cliente. */
  private _generateSessionId(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }
}
